import type {ReactNode, SyntheticEvent} from "react";
import {useMemo} from "react";
import cn from "classnames";

import {ChevronLeftIcon, ChevronRightIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {capitalize, range as nodashRange} from "@pg-mono/nodash";
import {PAGINATION_TEST_IDS} from "@pg-mono/test-ids";

import {PaginationSize} from "../types/PaginationSize";
import {getPaginationTextVariant} from "../utils/get_pagination_text_variant";
import {PageSeparator} from "./PageSeparator";
import {PaginationLink} from "./PaginationLink";

import * as styles from "./Pagination.module.css";

interface IProps {
    className?: string;
    currentPage: number;
    getPageLink?: (page: number) => string;
    onPageChange?: (page: number) => void;
    pageCount: number;
    size?: PaginationSize;
    siblingsCount?: number; // Number of pages that are shown before and after current page
}

type ClassName = keyof typeof styles;

const getPaginationSize = (size: PaginationSize) => `paginationSize${capitalize(size)}` as ClassName;
const getPaginationItemsSize = (size: PaginationSize) => `paginationItemsSize${capitalize(size)}` as ClassName;

const range = (start: number, end: number) => nodashRange(start, end + 1); // + 1 - for include last element in range

export const Pagination = (props: IProps) => {
    const {size = PaginationSize.LG, siblingsCount = 1} = props;

    const paginationTextVariant = getPaginationTextVariant(size);

    const onPageChange = (e: SyntheticEvent, page: number) => {
        if (props.onPageChange) {
            e.preventDefault();

            props.onPageChange(page);
        }
    };

    const renderPage = (page: number) => {
        const isActive = page === props.currentPage;
        const pageLink = props.getPageLink?.(page);

        return (
            <li key={page}>
                <PaginationLink
                    isActive={isActive}
                    data-testid={PAGINATION_TEST_IDS.GET_PAGE_LINK_TEST_ID(page)}
                    href={pageLink}
                    onClick={(e) => {
                        if (isActive) {
                            e.preventDefault();
                            return;
                        }

                        onPageChange(e, page);
                    }}
                    size={size}
                >
                    <Text as="span" variant={paginationTextVariant}>
                        {page}
                    </Text>
                </PaginationLink>
            </li>
        );
    };

    const renderSeparator = (key: string) => {
        return (
            <li key={key}>
                <PageSeparator size={size} textVariant={paginationTextVariant} />
            </li>
        );
    };

    const paginationItems: ReactNode[] = useMemo(() => {
        // Siblings count + firs page + last page + current page + separator * 2
        const totalPaginationItems = siblingsCount + 5;

        if (totalPaginationItems >= props.pageCount) {
            // Return list of pages without separators
            return range(1, props.pageCount).map(renderPage);
        }

        const currentPageLeftSiblingIndex = Math.max(props.currentPage - siblingsCount, 1);
        const currentPageRightSiblingIndex = Math.min(props.currentPage + siblingsCount, props.pageCount);

        // Show start separator only if there are more than two pages before current page
        const shouldShowStartSeparator = currentPageLeftSiblingIndex > 2;
        // Show end separator only if there are more than two pages after current page
        const shouldShowEndSeparator = currentPageRightSiblingIndex < props.pageCount - 2;

        const firstPageIndex = 1;
        const lastPageIndex = props.pageCount;

        if (!shouldShowStartSeparator && shouldShowEndSeparator) {
            const leftItemCount = 3 + 2 * siblingsCount; // Show 3 pages + all siblings (both left and right)
            const leftRange = range(1, leftItemCount);

            return [...leftRange.map(renderPage), renderSeparator("end"), renderPage(props.pageCount)];
        }

        if (shouldShowStartSeparator && !shouldShowEndSeparator) {
            const rightItemCount = 3 + 2 * siblingsCount; // Show 3 pages + all siblings (both left and right)
            const rightRange = range(props.pageCount - rightItemCount + 1, props.pageCount);

            return [renderPage(firstPageIndex), renderSeparator("start"), ...rightRange.map(renderPage)];
        }

        if (shouldShowStartSeparator && shouldShowEndSeparator) {
            const middleRange = range(currentPageLeftSiblingIndex, currentPageRightSiblingIndex);

            return [renderPage(firstPageIndex), renderSeparator("start"), ...middleRange.map(renderPage), renderSeparator("end"), renderPage(lastPageIndex)];
        }

        // If there are no separators, show all pages
        if (!shouldShowStartSeparator && !shouldShowEndSeparator) {
            return range(1, props.pageCount).map(renderPage);
        }

        return [];
    }, [props.pageCount, props.currentPage, size, props.onPageChange, props.getPageLink]);

    const prevPageNumber = props.currentPage - 1 || 1;
    const prevPageLink = props.getPageLink?.(prevPageNumber) ?? "";
    const prevPageIsDisabled = props.currentPage <= 1;

    const nextPageNumber = props.currentPage + 1;
    const nextPageLink = props.getPageLink?.(nextPageNumber) ?? "";
    const nextPageIsDisabled = props.currentPage >= props.pageCount;

    return props.pageCount > 1 ? (
        <nav className={cn(styles.pagination, styles[getPaginationSize(size)], props.className)} data-testid={PAGINATION_TEST_IDS.CONTAINER}>
            <PaginationLink
                className={styles.prev}
                data-testid={PAGINATION_TEST_IDS.PREV_PAGE_LINK}
                href={prevPageLink}
                isDisabled={prevPageIsDisabled}
                onClick={(e) => onPageChange(e, prevPageNumber)}
                rel="prev"
                size={size}
            >
                <ChevronLeftIcon />
            </PaginationLink>

            {/* Probably `7` was related to some special use case in `rp` app - left for consistency with previous package version. Needs to be verified. */}
            <ul className={cn(styles.paginationItems, styles[getPaginationItemsSize(size)], props.pageCount === 7 && styles.paginationItemsSizeCompressed)}>
                {paginationItems}
            </ul>

            <PaginationLink
                className={styles.next}
                data-testid={nextPageIsDisabled ? undefined : PAGINATION_TEST_IDS.NEXT_PAGE_LINK}
                href={nextPageLink}
                isDisabled={nextPageIsDisabled}
                onClick={(e) => onPageChange(e, nextPageNumber)}
                rel={nextPageIsDisabled ? undefined : "next"}
                size={size}
            >
                <ChevronRightIcon />
            </PaginationLink>
        </nav>
    ) : null;
};
import type {ReactNode, SyntheticEvent} from "react";
import {useMemo} from "react";
import cn from "classnames";

import {ChevronLeftIcon, ChevronRightIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {capitalize, range as nodashRange} from "@pg-mono/nodash";
import {PAGINATION_TEST_IDS} from "@pg-mono/test-ids";

import {PaginationSize} from "../types/PaginationSize";
import {getPaginationTextVariant} from "../utils/get_pagination_text_variant";
import {PageSeparator} from "./PageSeparator";
import {PaginationLink} from "./PaginationLink";

import * as styles from "./Pagination.module.css";

interface IProps {
    className?: string;
    currentPage: number;
    getPageLink?: (page: number) => string;
    onPageChange?: (page: number) => void;
    pageCount: number;
    size?: PaginationSize;
    siblingsCount?: number; // Number of pages that are shown before and after current page
}

type ClassName = keyof typeof styles;

const getPaginationSize = (size: PaginationSize) => `paginationSize${capitalize(size)}` as ClassName;
const getPaginationItemsSize = (size: PaginationSize) => `paginationItemsSize${capitalize(size)}` as ClassName;

const range = (start: number, end: number) => nodashRange(start, end + 1); // + 1 - for include last element in range

export const Pagination = (props: IProps) => {
    const {size = PaginationSize.LG, siblingsCount = 1} = props;

    const paginationTextVariant = getPaginationTextVariant(size);

    const onPageChange = (e: SyntheticEvent, page: number) => {
        if (props.onPageChange) {
            e.preventDefault();

            props.onPageChange(page);
        }
    };

    const renderPage = (page: number) => {
        const isActive = page === props.currentPage;
        const pageLink = props.getPageLink?.(page);

        return (
            <li key={page}>
                <PaginationLink
                    isActive={isActive}
                    data-testid={PAGINATION_TEST_IDS.GET_PAGE_LINK_TEST_ID(page)}
                    href={pageLink}
                    onClick={(e) => {
                        if (isActive) {
                            e.preventDefault();
                            return;
                        }

                        onPageChange(e, page);
                    }}
                    size={size}
                >
                    <Text as="span" variant={paginationTextVariant}>
                        {page}
                    </Text>
                </PaginationLink>
            </li>
        );
    };

    const renderSeparator = (key: string) => {
        return (
            <li key={key}>
                <PageSeparator size={size} textVariant={paginationTextVariant} />
            </li>
        );
    };

    const paginationItems: ReactNode[] = useMemo(() => {
        // Siblings count + firs page + last page + current page + separator * 2
        const totalPaginationItems = siblingsCount + 5;

        if (totalPaginationItems >= props.pageCount) {
            // Return list of pages without separators
            return range(1, props.pageCount).map(renderPage);
        }

        const currentPageLeftSiblingIndex = Math.max(props.currentPage - siblingsCount, 1);
        const currentPageRightSiblingIndex = Math.min(props.currentPage + siblingsCount, props.pageCount);

        // Show start separator only if there are more than two pages before current page
        const shouldShowStartSeparator = currentPageLeftSiblingIndex > 2;
        // Show end separator only if there are more than two pages after current page
        const shouldShowEndSeparator = currentPageRightSiblingIndex < props.pageCount - 2;

        const firstPageIndex = 1;
        const lastPageIndex = props.pageCount;

        if (!shouldShowStartSeparator && shouldShowEndSeparator) {
            const leftItemCount = 3 + 2 * siblingsCount; // Show 3 pages + all siblings (both left and right)
            const leftRange = range(1, leftItemCount);

            return [...leftRange.map(renderPage), renderSeparator("end"), renderPage(props.pageCount)];
        }

        if (shouldShowStartSeparator && !shouldShowEndSeparator) {
            const rightItemCount = 3 + 2 * siblingsCount; // Show 3 pages + all siblings (both left and right)
            const rightRange = range(props.pageCount - rightItemCount + 1, props.pageCount);

            return [renderPage(firstPageIndex), renderSeparator("start"), ...rightRange.map(renderPage)];
        }

        if (shouldShowStartSeparator && shouldShowEndSeparator) {
            const middleRange = range(currentPageLeftSiblingIndex, currentPageRightSiblingIndex);

            return [renderPage(firstPageIndex), renderSeparator("start"), ...middleRange.map(renderPage), renderSeparator("end"), renderPage(lastPageIndex)];
        }

        // If there are no separators, show all pages
        if (!shouldShowStartSeparator && !shouldShowEndSeparator) {
            return range(1, props.pageCount).map(renderPage);
        }

        return [];
    }, [props.pageCount, props.currentPage, size, props.onPageChange, props.getPageLink]);

    const prevPageNumber = props.currentPage - 1 || 1;
    const prevPageLink = props.getPageLink?.(prevPageNumber) ?? "";
    const prevPageIsDisabled = props.currentPage <= 1;

    const nextPageNumber = props.currentPage + 1;
    const nextPageLink = props.getPageLink?.(nextPageNumber) ?? "";
    const nextPageIsDisabled = props.currentPage >= props.pageCount;

    return props.pageCount > 1 ? (
        <nav className={cn(styles.pagination, styles[getPaginationSize(size)], props.className)} data-testid={PAGINATION_TEST_IDS.CONTAINER}>
            <PaginationLink
                className={styles.prev}
                data-testid={PAGINATION_TEST_IDS.PREV_PAGE_LINK}
                href={prevPageLink}
                isDisabled={prevPageIsDisabled}
                onClick={(e) => onPageChange(e, prevPageNumber)}
                rel="prev"
                size={size}
            >
                <ChevronLeftIcon />
            </PaginationLink>

            {/* Probably `7` was related to some special use case in `rp` app - left for consistency with previous package version. Needs to be verified. */}
            <ul className={cn(styles.paginationItems, styles[getPaginationItemsSize(size)], props.pageCount === 7 && styles.paginationItemsSizeCompressed)}>
                {paginationItems}
            </ul>

            <PaginationLink
                className={styles.next}
                data-testid={nextPageIsDisabled ? undefined : PAGINATION_TEST_IDS.NEXT_PAGE_LINK}
                href={nextPageLink}
                isDisabled={nextPageIsDisabled}
                onClick={(e) => onPageChange(e, nextPageNumber)}
                rel={nextPageIsDisabled ? undefined : "next"}
                size={size}
            >
                <ChevronRightIcon />
            </PaginationLink>
        </nav>
    ) : null;
};
